import { useState, useCallback } from 'react';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { useEngagementMetricsLogData } from '../../../hooks/useEngagementMetricsLogData';
import { useEngagementMetricsExport } from '../../../hooks/useEngagementMetricsExport';
import { ErrorState } from '../../analytics/shared/ErrorState';
import { ExportButton } from '../../analytics/shared/ExportButton';
import { PIIExportDialog } from '../../analytics/shared/PIIExportDialog';
import { EngagementLogFilters } from '../log/EngagementLogFilters';
import { EngagementLogTable } from '../log/EngagementLogTable';
import type { EngagementMetricsQueryParams } from '../../../types/engagement-metrics.types';

type EngagementMetricsLogTabProps = {
  queryParams: EngagementMetricsQueryParams;
  isActive?: boolean;
};

/**
 * Engagements Log sub-tab: search/quick-filter/table wiring for the cursor-paginated
 * `GET /admin/analytics/engagement-metrics/log` endpoint, plus CSV export of the same
 * filtered rows via `GET .../engagement-metrics/export` (ticket 16).
 */
export const EngagementMetricsLogTab = ({
  queryParams,
  isActive = true,
}: EngagementMetricsLogTabProps) => {
  const log = useEngagementMetricsLogData(queryParams, isActive);
  const [showPIIDialog, setShowPIIDialog] = useState(false);
  const { exportCSV, isExporting } = useEngagementMetricsExport();

  const handleExportConfirm = useCallback(async () => {
    await exportCSV(log.exportParams);
    setShowPIIDialog(false);
  }, [exportCSV, log.exportParams]);

  if (!isActive) return null;

  if (log.isError) {
    return (
      <div className="pt-6" data-testid="engagement-metrics-log-tab">
        <ErrorState
          message="Failed to load the engagements log. Please try again."
          onRetry={() => log.refetch()}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4 pt-6" data-testid="engagement-metrics-log-tab">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <EngagementLogFilters
          search={log.search}
          onSearchChange={log.setSearch}
          billableFilter={log.billableFilter}
          onBillableFilterChange={log.setBillableFilter}
          reportingType={log.reportingType}
          onReportingTypeChange={log.setReportingType}
          initiatorRole={log.initiatorRole}
          onInitiatorRoleChange={log.setInitiatorRole}
        />
        <ExportButton
          onClick={() => setShowPIIDialog(true)}
          isLoading={isExporting}
          label="Export CSV"
        />
      </div>

      {log.isLoading ? (
        <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton
              key={i}
              className={`h-12 w-full rounded-none ${i > 0 ? 'border-t border-gray-100' : ''}`}
            />
          ))}
        </div>
      ) : (
        <EngagementLogTable
          rows={log.items}
          isLoading={log.isLoading}
          hasNextPage={log.hasNextPage}
          isFetchingNextPage={log.isFetchingNextPage}
          onLoadMore={() => log.fetchNextPage()}
        />
      )}

      <PIIExportDialog
        open={showPIIDialog}
        onOpenChange={setShowPIIDialog}
        onConfirm={handleExportConfirm}
        exportType="Engagements Log"
        isLoading={isExporting}
      />
    </div>
  );
};
